import { useState, useEffect } from 'react';
import {
  Calculator, Home, Wrench, BarChart3, Clock, Bell, Brain,
  Code, Copy, Check, ExternalLink, Eye, EyeOff, Settings, ChevronRight
} from 'lucide-react';

const API = getApiUrl();
import getApiUrl from '../../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const WIDGET_REGISTRY = [
  {
    id: 'simulateur-mpa',
    name: 'Simulateur MaPrimeAdapt\'',
    description: 'Simulateur d\'éligibilité et calcul des aides MaPrimeAdapt\' pour l\'adaptation du logement des seniors et PMR.',
    icon: Calculator,
    color: 'violet',
    category: 'Simulateurs',
    component: 'SimulateurMPAWidget',
    embedReady: true,
  },
  {
    id: 'simulateur-reno-energie',
    name: 'Simulateur Rénovation Énergétique',
    description: 'Simulateur d\'aides pour la rénovation énergétique : PAC, isolation, chauffage.',
    icon: Home,
    color: 'orange',
    category: 'Simulateurs',
    component: 'SimulateurRenoEnergieWidget',
    embedReady: true,
  },
  {
    id: 'travaux-confort',
    name: 'Devis Travaux de Confort',
    description: 'Estimateur de travaux de confort : salle de bain, volets, carrelage, peinture, etc.',
    icon: Wrench,
    color: 'teal',
    category: 'Simulateurs',
    component: 'TravauxConfortWidget',
    embedReady: true,
  },
  {
    id: 'pipeline',
    name: 'Pipeline Commercial',
    description: 'Vue pipeline des leads et clients par étape du parcours commercial.',
    icon: BarChart3,
    color: 'blue',
    category: 'CRM',
    component: 'PipelineWidget',
    embedReady: false,
  },
  {
    id: 'mes-rappels',
    name: 'Mes Rappels',
    description: 'Widget de rappels planifiés : rappels du jour, de la semaine, en retard.',
    icon: Clock,
    color: 'amber',
    category: 'CRM',
    component: 'MesRappelsWidget',
    embedReady: false,
  },
  {
    id: 'fatima-alerts',
    name: 'Alertes Fatima IA',
    description: 'Notifications intelligentes IA : alertes appels, analyses sentiment, suggestions.',
    icon: Bell,
    color: 'red',
    category: 'IA',
    component: 'FatimaAlertsWidget',
    embedReady: false,
  },
  {
    id: 'simulateur-aides',
    name: 'Simulateur Aides Global',
    description: 'Dashboard complet de simulation d\'aides : MPA, CEE, éco-PTZ, TVA réduite.',
    icon: Brain,
    color: 'emerald',
    category: 'Simulateurs',
    component: 'SimulateurAidesDashboard',
    embedReady: true,
  },
  {
    id: 'nouveau-lead',
    name: 'Formulaire Nouveau Lead',
    description: 'Formulaire complet de création de lead : qualification, simulateurs intégrés (MPA, Réno, Confort), planification RDV.',
    icon: Calculator,
    color: 'emerald',
    category: 'CRM',
    component: 'NouveauLeadWidget',
    embedReady: true,
  },
];

const colorMap = {
  violet: { bg: 'bg-violet-500/10', border: 'border-violet-500/30', text: 'text-violet-400', badge: 'bg-violet-500/20' },
  orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', badge: 'bg-orange-500/20' },
  teal: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', badge: 'bg-teal-500/20' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', badge: 'bg-blue-500/20' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', badge: 'bg-amber-500/20' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', badge: 'bg-red-500/20' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', badge: 'bg-emerald-500/20' },
};

export default function WidgetRegistryManager() {
  const [societes, setSocietes] = useState([]);
  const [selectedSociete, setSelectedSociete] = useState('');
  const [embedWidget, setEmbedWidget] = useState(null);
  const [copied, setCopied] = useState(false);
  const [widgetStates, setWidgetStates] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${API}/api/societes`);
        if (r.ok) {
          const data = await r.json();
          setSocietes(data);
          if (data.length > 0) setSelectedSociete(data[0].id);
        }
      } catch (e) { console.error(e); }
      // Load widget states
      try {
        const r = await authFetch(`${API}/api/widgets/config`);
        if (r.ok) {
          const data = await r.json();
          setWidgetStates(data.config || {});
        }
      } catch (e) {}
    })();
  }, []);

  const toggleWidgetActive = async (widgetId) => {
    const newState = { ...widgetStates, [widgetId]: { ...widgetStates[widgetId], active: !widgetStates[widgetId]?.active } };
    setWidgetStates(newState);
    try {
      await authFetch(`${API}/api/widgets/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: newState })
      });
    } catch (e) { console.error(e); }
  };

  const getEmbedCode = (widget) => {
    const base = window.location.origin;
    const url = `${base}/widget/${widget.id}${selectedSociete ? `?societe=${selectedSociete}` : ''}`;
    return `<iframe
  src="${url}"
  width="100%"
  height="700"
  style="border: none; border-radius: 12px;"
  title="${widget.name}"
  allow="clipboard-write"
></iframe>`;
  };

  const getEmbedUrl = (widget) => {
    const base = window.location.origin;
    return `${base}/widget/${widget.id}${selectedSociete ? `?societe=${selectedSociete}` : ''}`;
  };

  const copyEmbed = (widget) => {
    navigator.clipboard.writeText(getEmbedCode(widget));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const categories = [...new Set(WIDGET_REGISTRY.map(w => w.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Widgets Registry</h2>
          <p className="text-sm text-slate-400 mt-1">{WIDGET_REGISTRY.length} widgets disponibles — {WIDGET_REGISTRY.filter(w => w.embedReady).length} embed-ready</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-slate-500">Societe :</label>
          <select
            data-testid="widget-societe-select"
            value={selectedSociete}
            onChange={e => setSelectedSociete(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-white"
          >
            {societes.map(s => (
              <option key={s.id} value={s.id}>{s.raison_sociale}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Widget cards by category */}
      {categories.map(cat => (
        <div key={cat}>
          <h3 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wider">{cat}</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {WIDGET_REGISTRY.filter(w => w.category === cat).map(widget => {
              const c = colorMap[widget.color] || colorMap.blue;
              const Icon = widget.icon;
              const isActive = widgetStates[widget.id]?.active !== false;
              const isEmbedOpen = embedWidget === widget.id;

              return (
                <div
                  key={widget.id}
                  data-testid={`widget-card-${widget.id}`}
                  className={`${c.bg} border ${c.border} rounded-xl p-4 transition-all ${!isActive ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${c.badge} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${c.text}`} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-white">{widget.name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${c.badge} ${c.text}`}>{widget.category}</span>
                          {widget.embedReady && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Embed Ready</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      data-testid={`widget-toggle-${widget.id}`}
                      onClick={() => toggleWidgetActive(widget.id)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${isActive ? 'bg-emerald-500' : 'bg-slate-700'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  <p className="text-xs text-slate-400 mb-3">{widget.description}</p>

                  <div className="flex items-center gap-2">
                    {widget.embedReady && (
                      <button
                        data-testid={`widget-embed-${widget.id}`}
                        onClick={() => setEmbedWidget(isEmbedOpen ? null : widget.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          isEmbedOpen ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                        }`}
                      >
                        <Code className="w-3 h-3" /> Embed
                      </button>
                    )}
                    <a
                      href={getEmbedUrl(widget)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 hover:bg-slate-700 rounded-lg text-xs font-medium transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" /> Preview
                    </a>
                  </div>

                  {/* Embed code panel */}
                  {isEmbedOpen && (
                    <div className="mt-3 bg-slate-900 rounded-lg p-3 border border-slate-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-400">Code iframe a copier :</span>
                        <button
                          data-testid={`widget-copy-${widget.id}`}
                          onClick={() => copyEmbed(widget)}
                          className="flex items-center gap-1 px-2 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded text-xs"
                        >
                          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {copied ? 'Copie !' : 'Copier'}
                        </button>
                      </div>
                      <pre className="text-[11px] text-emerald-400 font-mono bg-slate-950 rounded p-2 overflow-x-auto whitespace-pre-wrap">{getEmbedCode(widget)}</pre>
                      <div className="mt-2 text-[10px] text-slate-500">
                        URL directe : <a href={getEmbedUrl(widget)} target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">{getEmbedUrl(widget)}</a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export { WIDGET_REGISTRY };
